// [PHASE B / #019 — B2-5]: Single source of truth for the BASE_ITEMS
//   storage taxonomy. Pre-fix the same taxonomy was hardcoded in three
//   places (initializeSources.js's formattedItems template,
//   equipmentResolver.js's findInBaseItems priority list, and
//   SearchableItemSelector.js's getCategoryIcon map) and they had
//   drifted: the source-template had 9 entries with 4 phantoms
//   (treasure / scrolls / wondrous / potions / ammunition) that no item
//   ever populated, the resolver array had a `magic_items` typo for
//   the actual `magical_items` storage key, and the icon map had its
//   own list. The user's May 5 smoke test surfaced the consequence as
//   "Treasure / Wondrous / Scrolls categories show 0 items."
//
// Per the long-term data-block vision (`feedback_long_term_data_block_vision.md`),
// the taxonomy is itself a data block — categories are first-class
// entries with id (matches gameData.js BASE_ITEMS storage key), label,
// icon, and order. New TTRPG content sets can declare new category
// entries without touching every consumer.
//
// What "category" means here:
//   - This is the STORAGE taxonomy (the keys at `BASE_ITEMS[id]`).
//   - It is NOT the item-type tag taxonomy in `enums.js ITEM_CATEGORIES`,
//     which records the singular type a row may carry (e.g. 'wondrous_items'
//     as a sub-type tag inside `magical_items`). Those are different
//     concepts and both stay.

export const BASE_ITEM_CATEGORIES = Object.freeze([
  Object.freeze({ id: 'weapons',          label: 'Weapons',          icon: '⚔️',  order: 1  }),
  Object.freeze({ id: 'ammunition',       label: 'Ammunition',       icon: '🏹',  order: 2  }),
  Object.freeze({ id: 'armor',            label: 'Armor',            icon: '🛡️',  order: 3  }),
  Object.freeze({ id: 'items',            label: 'Items',            icon: '🎒',  order: 4  }),
  Object.freeze({ id: 'containers',       label: 'Containers',       icon: '📦',  order: 5  }),
  Object.freeze({ id: 'consumables',      label: 'Consumables',      icon: '🍷',  order: 6  }),
  Object.freeze({ id: 'food',             label: 'Food',             icon: '🍞',  order: 7  }),
  Object.freeze({ id: 'tools',            label: 'Tools',            icon: '🔧',  order: 8  }),
  Object.freeze({ id: 'spell_components', label: 'Spell Components', icon: '🧪',  order: 9  }),
  Object.freeze({ id: 'jewelry',          label: 'Jewelry',          icon: '💍',  order: 10 }),
  Object.freeze({ id: 'art_objects',      label: 'Art Objects',      icon: '🖼️',  order: 11 }),
  Object.freeze({ id: 'gems',             label: 'Gems',             icon: '💎',  order: 12 }),
  Object.freeze({ id: 'trade_goods',      label: 'Trade Goods',      icon: '🛍️',  order: 13 }),
  Object.freeze({ id: 'trinkets',         label: 'Trinkets',         icon: '🎲',  order: 14 }),
  Object.freeze({ id: 'magical_items',    label: 'Magical Items',    icon: '✨',  order: 15 }),
]);

export const BASE_ITEM_CATEGORY_IDS = BASE_ITEM_CATEGORIES.map((c) => c.id);

const _byId = new Map(BASE_ITEM_CATEGORIES.map((c) => [c.id, c]));

/**
 * Look up a category descriptor by its id. Returns null for unknown ids
 * (custom-source categories that aren't in the data block).
 */
export function getBaseItemCategory(id) {
  if (typeof id !== 'string' || !id) return null;
  return _byId.get(id) || null;
}

/**
 * Display label for a category id. Falls back to the id itself for
 * unknown categories so custom sources still render something sensible.
 */
export function getBaseItemCategoryLabel(id) {
  const cat = getBaseItemCategory(id);
  return cat ? cat.label : (typeof id === 'string' ? id : '');
}

/**
 * Display icon for a category id. Falls back to a generic page icon for
 * unknown categories.
 */
export function getBaseItemCategoryIcon(id) {
  const cat = getBaseItemCategory(id);
  return cat ? cat.icon : '📄';
}
